/**
 * Vision Optique — Theme Runtime Entry Point
 * ------------------------------------------------
 * Sprint 1, Batch 3 (JS Foundation). The ONE global JavaScript entry
 * point, loaded once from layout/theme.liquid
 * (`{{ 'theme.js' | asset_url }}`, type="module" — already scaffolded
 * there since Sprint 1, Batch 1 for exactly this moment). No other
 * script tag in the theme should be a second, independent runtime
 * entry point.
 *
 * Responsibilities, per this batch's brief: application bootstrap,
 * module (component) registration, initialization lifecycle, error
 * isolation, Shopify Theme Editor compatibility, and a small custom
 * events surface. No business logic — this file does not know
 * anything about what a header or announcement bar actually does, only
 * that they exist and how to reach their modules.
 *
 * A note on "error isolation" and static imports: this file imports
 * dom.js/init.js/events.js as static ES module dependencies of the
 * runtime itself — a syntax error in one of those foundational files
 * is a development-time bug the module system cannot route around
 * (there is no meaningful way to "isolate" a broken foundation from
 * the code built on it). The error isolation this batch actually asks
 * for is per-COMPONENT initialization failure (one broken section
 * must not break another), which is implemented in init.js's
 * mountElement — each component is loaded via a lazy `import()` inside
 * its own try/catch, so a broken component module fails independently
 * of every other one.
 */

import { registerComponentLoader, mountAll, bindThemeEditorEvents } from './init.js';
import { on as onDocumentEvent, emit as emitDocumentEvent } from './events.js';

// Stabilization fix (Priority 1): these were originally registered
// via a lazy `() => import('./x.js')` loader per component. Shopify's
// CDN asset pipeline was found to rewrite those dynamic `import()`
// call sites into `require(...)` at serve time (an esbuild-style CJS
// interop transform), which throws `ReferenceError: require is not
// defined` in this browser `type="module"` context — every component
// failed to initialize as a result. Static top-level `import`
// statements were confirmed to survive that same transform untouched,
// so every component module is now imported statically here instead.
// This trades lazy-loading for correctness; each module is tiny and
// this is the minimal change that avoids the broken code path without
// touching Shopify's asset pipeline (which is not configurable from
// theme code). See init.js's mountElement — it still calls the
// registered loader as `await loader()` and reads `.default`, so the
// loader's shape (a function returning a promise of a
// `{ default: ComponentClass }`-like object) is preserved exactly;
// only how each module's code gets onto the page changed.
import HeaderComponent from './sections-header.js';
import AnnouncementBarComponent from './sections-announcement-bar.js';
import NavigationDrawerComponent from './components-navigation-drawer.js';
// Cache-bust (Phase 1 gallery/Recently Viewed fix, 30 July 2026): this
// theme's asset pipeline gives no version query string to internal
// relative imports (unlike `theme.js`'s own asset_url-derived URL or
// any `asset_url | stylesheet_tag` CSS include) — see this file's own
// header note on the static-import architecture. That is a real gap:
// Shopify's CDN was found caching `/assets/sections-main-product.js`
// at the edge even after a fresh upload replaced its stored content
// (confirmed via a direct Admin API read showing the new content
// live, while a cache-busted `fetch()` to the CDN URL still returned
// the old bytes). Appending a query string to the import specifier
// forces the browser (and the CDN, which keys on the full URL) to
// treat this as a distinct resource, bypassing the stale cached
// response. Bump this value on any future edit to this module.
import MainProductComponent from './sections-main-product.js?v=2';
import ProductRecommendationsComponent from './sections-product-recommendations.js';
import PredictiveSearchComponent from './sections-predictive-search.js';
import CartDrawerComponent from './sections-cart-drawer.js';
import MainCartComponent from './sections-main-cart.js';

/**
 * Module registration. Each entry maps the `data-component` value a
 * section's markup already carries to a loader for its module — no
 * manual `new Component(el)` call lives inside any section file
 * (that self-bootstrap has been removed from sections-header.js and
 * sections-announcement-bar.js). Adding a future interactive section
 * is one static import + one line here, not a change to this file's
 * structure.
 */
registerComponentLoader('header', () => Promise.resolve({ default: HeaderComponent }));
registerComponentLoader('announcement-bar', () => Promise.resolve({ default: AnnouncementBarComponent }));
registerComponentLoader('navigation-drawer', () => Promise.resolve({ default: NavigationDrawerComponent }));
registerComponentLoader('main-product', () => Promise.resolve({ default: MainProductComponent }));
registerComponentLoader('product-recommendations', () => Promise.resolve({ default: ProductRecommendationsComponent }));
registerComponentLoader('predictive-search', () => Promise.resolve({ default: PredictiveSearchComponent }));
registerComponentLoader('cart-drawer', () => Promise.resolve({ default: CartDrawerComponent }));
registerComponentLoader('main-cart', () => Promise.resolve({ default: MainCartComponent }));

/**
 * Theme-wide custom events surface — a thin, document-scoped
 * convenience wrapper around events.js's generic emit()/on(), so any
 * future component can broadcast or listen for app-wide events without
 * each one separately deciding what the "bus" element should be.
 */
export function on(type, handler) {
  return onDocumentEvent(document, type, handler);
}

export function emit(type, detail) {
  return emitDocumentEvent(document, type, detail);
}

function boot() {
  mountAll(document);
  bindThemeEditorEvents();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
